import { Helmet } from "react-helmet-async";
import {
  SITE_NAME,
  SITE_LOCALE,
  SITE_LOCALE_ALT,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  TITLE_TEMPLATE,
  OG_IMAGE,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_ALT,
  TWITTER_SITE,
  absoluteUrl,
} from "@/lib/seo/site";

// Per-page metadata: title, description, canonical, Open Graph, Twitter card,
// robots directives, and any JSON-LD passed via `jsonLd`. Client-rendered via
// react-helmet-async (no SSR) — sufficient for social/link unfurlers and
// modern crawlers that execute JS.
//
// Props:
//   title         page title (run through the "%s | SocialLead AI" template
//                  unless `titleFull` is set)
//   titleFull     use this exact string as the <title> (no template)
//   description   meta description
//   path          route path ("/contact") → canonical + og:url
//   image         social image URL (defaults to the branded OG banner)
//   type          og:type ("website" | "article" | ...)
//   noindex       emit robots noindex,nofollow
//   jsonLd        object or array of JSON-LD documents
//   children      extra <meta>/<link> tags
export default function Seo({
  title,
  titleFull,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = OG_IMAGE,
  imageAlt = OG_IMAGE_ALT,
  type = "website",
  noindex = false,
  jsonLd,
  children,
}) {
  const canonical = absoluteUrl(path);
  const computedTitle = titleFull
    ? titleFull
    : title
      ? TITLE_TEMPLATE.replace("%s", title)
      : DEFAULT_TITLE;

  const robots = noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  const docs = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{computedTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={computedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:url" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:locale" content={SITE_LOCALE} />
      <meta property="og:locale:alternate" content={SITE_LOCALE_ALT} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={computedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />
      {TWITTER_SITE ? <meta name="twitter:site" content={TWITTER_SITE} /> : null}
      {TWITTER_SITE ? <meta name="twitter:creator" content={TWITTER_SITE} /> : null}

      {docs.filter(Boolean).map((doc, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(doc)}
        </script>
      ))}

      {children}
    </Helmet>
  );
}
